/**
 * Manuscript: the writing column. Children are paragraphs (<p>); give the paragraph being
 * edited class "is-editing". Set editable to make the text a contenteditable region.
 * (cockatrice-ui `src/ui/Manuscript.jsx`, ported to hyperscript; PLAN.md §0)
 * `titleId` and `titleFocusable` (the read view's move, PLAN.md) let a page name the title and move the focus there, as
 * Section's `headingId` and `headingFocusable` do.
 */
import './css/manuscript.css';
import { mergeProps, splitProps } from 'solid-js';
import { el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export interface ManuscriptProps extends Attributes {
  /** Small line above the title, e.g. "Chapter 7". */
  readonly kicker?: string | undefined;
  /** Chapter title (h1). */
  readonly title?: string | undefined;
  /** The title's id, for a page whose skip link or tests name it. */
  readonly titleId?: string | undefined;
  /** The title takes the focus from script (tabindex -1), as a page's heading does when the page opens. */
  readonly titleFocusable?: boolean | undefined;
  /** Make the body a contenteditable textbox. */
  readonly editable?: boolean | undefined;
  /** Text size in px; follows the Text size setting. Default 20 (18 on phones). */
  readonly fontSize?: number | undefined;
  /** Accessible name of the editable region. Default "Text of {title}". */
  readonly label?: string | undefined;
  /** Input handler on the editable body. */
  readonly onInput?: ((e: InputEvent) => void) | undefined;
  /** Paragraphs; give the one being edited class "is-editing". */
  readonly children?: Child;
  /** Extra classes. */
  readonly class?: string | undefined;
  /** Inline styles, merged after the text size. */
  readonly style?: Readonly<Record<string, string>> | undefined;
}

const filled = (value: string | undefined): value is string => value !== undefined && value !== '';

export default function Manuscript(props: ManuscriptProps): Child {
  const [local, rest] = splitProps(props, ['kicker', 'title', 'titleId', 'titleFocusable', 'editable', 'fontSize', 'label', 'children', 'class', 'style', 'onInput']);
  const editable = (): boolean => local.editable === true;
  const label = (): string | undefined => {
    if (!editable()) return undefined;
    if (filled(local.label)) return local.label;
    return filled(local.title) ? uiText()('ui.manuscript.text-of', { title: local.title }) : uiText()('ui.manuscript.label');
  };
  return el(
    'article',
    mergeProps({
      class: () => `ui-manuscript${filled(local.class) ? ` ${local.class}` : ''}`,
      style: () => ({ ...(local.fontSize !== undefined && local.fontSize !== 0 ? { '--ui-ms-size': `${local.fontSize}px` } : {}), ...(local.style ?? {}) }),
    }, rest),
    when(
      () => filled(local.kicker),
      () => el('p', { class: 'ui-manuscript__kicker' }, () => local.kicker),
    ),
    when(
      () => filled(local.title),
      () =>
        el(
          'h1',
          { class: 'ui-manuscript__title', id: () => local.titleId, tabIndex: () => (local.titleFocusable === true ? -1 : undefined) },
          () => local.title,
        ),
    ),
    el(
      'div',
      {
        class: 'ui-manuscript__body',
        contenteditable: () => (editable() ? 'true' : undefined),
        role: () => (editable() ? 'textbox' : undefined),
        'aria-multiline': () => (editable() ? 'true' : undefined),
        'aria-label': label,
        spellcheck: () => (editable() ? true : undefined),
        onInput: (e: InputEvent) => {
          local.onInput?.(e);
        },
      },
      () => local.children,
    ),
  );
}
